import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Building2, Users, Zap, Shield, Star, ArrowRight, CheckCircle2, Globe, BriefcaseBusiness, Search, ShieldCheck, FileText, Video, MessageSquare, Crown, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

const HERO_BG = "https://images.unsplash.com/photo-1771812967957-2e32963cc31d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxob3NwaXRhbGl0eSUyMHdvcmtlciUyMGhvdGVsJTIwc3RhZmYlMjBwcm9mZXNzaW9uYWwlMjBzbWlsaW5nfGVufDB8fHx8MTc4NTg2MTk0N3ww&ixlib=rb-4.1.0&q=85";

const FEATURE_ICONS = [BriefcaseBusiness, FileText, Globe, MessageSquare, Star, Shield];
const FEATURE_KEYS = ["jobs", "profile", "europe", "connect", "ai", "gdpr"];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);

  const handlePlanClick = (plan) => {
    if (plan === "premium") {
      setShowComingSoon(true);
      return;
    }
    navigate(`/for-hotels`);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-900/95 backdrop-blur-md border-b border-navy-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16">
          <Logo size="md" className="text-white flex-shrink-0 mr-8" />
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors font-body">{t("nav.features")}</a>
            <a href="#how" className="text-sm text-slate-400 hover:text-white transition-colors font-body">{t("nav.how")}</a>
            <a href="/about" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="nav-about-link">{t("nav.about")}</a>
            <a href="/contact" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="nav-contact-link">{t("contact.link")}</a>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <LanguageSelector variant="dark" />
            <Button variant="outline" size="sm" className="hidden md:inline-flex items-center gap-1.5 border-slate-500 text-white font-semibold hover:bg-navy-800 hover:border-slate-300 text-sm rounded-sm"
              onClick={() => navigate("/for-hotels")} data-testid="nav-hotel-register-btn">
              <Building2 className="w-3.5 h-3.5" /> {t("nav.hotel_register")}
            </Button>
            <Button variant="ghost" className="hidden md:inline-flex text-slate-300 hover:text-white hover:bg-navy-800 text-sm" onClick={() => navigate("/auth")} data-testid="nav-login-btn">
              {t("nav.login")}
            </Button>
            <Button className="hidden md:inline-flex bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm text-sm md:text-base" onClick={() => navigate("/auth?mode=register&role=candidate")} data-testid="nav-register-btn">
              {t("nav.register")}
            </Button>
            <button className="md:hidden text-white p-2" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
              {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        {mobileMenu && (
          <div className="md:hidden bg-navy-900 border-t border-navy-800 px-4 pb-4 space-y-1 animate-fade-in" data-testid="mobile-menu">
            <a href="#features" onClick={() => setMobileMenu(false)} className="block py-2.5 text-sm text-slate-300 hover:text-white font-body">{t("nav.features")}</a>
            <a href="#how" onClick={() => setMobileMenu(false)} className="block py-2.5 text-sm text-slate-300 hover:text-white font-body">{t("nav.how")}</a>
            <a href="/about" className="block py-2.5 text-sm text-slate-300 hover:text-white font-body">{t("nav.about")}</a>
            <a href="/contact" className="block py-2.5 text-sm text-slate-300 hover:text-white font-body">{t("contact.link")}</a>
            <div className="border-t border-navy-800 pt-3 mt-2 space-y-2">
              <Button variant="outline" size="sm" className="w-full justify-center border-slate-500 text-white font-semibold hover:bg-navy-800 text-sm rounded-sm"
                onClick={() => { navigate("/for-hotels"); setMobileMenu(false); }}>
                <Building2 className="w-3.5 h-3.5 mr-1.5" /> {t("nav.hotel_register")}
              </Button>
              <Button variant="ghost" className="w-full justify-center text-slate-300 hover:text-white hover:bg-navy-800 text-sm"
                onClick={() => { navigate("/auth"); setMobileMenu(false); }}>
                {t("nav.login")}
              </Button>
              <Button className="w-full justify-center bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm text-sm"
                onClick={() => { navigate("/auth?mode=register&role=candidate"); setMobileMenu(false); }}>
                {t("nav.register")}
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero — Candidate-focused */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-navy-950">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Hospitality professional" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-32">
          <div className="max-w-2xl">
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-400 border-emerald-500/20 font-body">
              {t("hero.badge")}
            </Badge>
            <h1 className="font-sans font-bold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-tight mb-6 animate-fade-in">
              {t("hero.title1")}
              <span className="gold-text block">{t("hero.title2")}</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 font-body leading-relaxed mb-8 max-w-lg">
              {t("hero.desc")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm shadow-lg text-base px-8 h-12"
                onClick={() => navigate("/auth?mode=register&role=candidate")} data-testid="hero-candidate-cta">
                <Users className="mr-2 w-4 h-4" /> {t("hero.candidate_cta")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm text-slate-400 font-body">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t("hero.free")}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t("hero.no_commit")}</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> {t("hero.gdpr")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features — Candidate-focused */}
      <section id="features" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-500/10 text-emerald-600 border-emerald-500/20 font-body">{t("nav.features")}</Badge>
            <h2 className="font-sans font-bold text-3xl tracking-tight text-slate-900 mb-4">{t("feat.title")}</h2>
            <p className="text-base text-slate-500 font-body max-w-2xl mx-auto">{t("feat.desc")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURE_KEYS.map((key, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <div key={key} className={`bg-white border border-slate-200 rounded-lg p-6 card-lift stagger-${i+1} animate-fade-in`}>
                  <div className="w-10 h-10 rounded-sm bg-navy-900 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="font-sans font-semibold text-lg text-slate-900 mb-2">{t(`feat.${key}`)}</h3>
                  <p className="text-sm text-slate-500 font-body leading-relaxed">{t(`feat.${key}_desc`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works — Candidate-focused */}
      <section id="how" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="font-sans font-bold text-3xl tracking-tight text-slate-900 mb-4">{t("how.title")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-600 font-sans font-bold text-xl flex items-center justify-center mx-auto mb-4">0{s}</div>
                <h3 className="font-sans font-semibold text-xl text-slate-900 mb-2">{t(`how.step${s}_title`)}</h3>
                <p className="text-sm text-slate-500 font-body">{t(`how.step${s}_desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Simple CTA */}
      <section id="pricing" className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-sans font-bold text-3xl tracking-tight text-slate-900 mb-4">{t("landing.free_cta_title")}</h2>
          <p className="text-base text-slate-500 font-body mb-8">{t("landing.free_cta_desc")}</p>
          <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-sm px-10 h-12 text-base"
            onClick={() => navigate("/auth?mode=register&role=candidate")} data-testid="landing-free-cta">
            {t("landing.free_cta_btn")} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Coming Soon Dialog */}
      <Dialog open={showComingSoon} onOpenChange={setShowComingSoon}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="coming-soon-dialog">
          <DialogTitle className="sr-only">{t("premium.coming_soon_title")}</DialogTitle>
          <div className="bg-navy-900 px-6 pt-7 pb-5 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gold-500/20 flex items-center justify-center">
              <Crown className="w-7 h-7 text-gold-400" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white mb-1">{t("premium.coming_soon_title")}</h2>
            <p className="text-sm text-slate-300 font-body">{t("premium.coming_soon_desc")}</p>
          </div>
          <div className="px-6 py-5">
            <Button variant="outline" className="w-full" onClick={() => setShowComingSoon(false)} data-testid="coming-soon-close-btn">
              {t("common.close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-navy-900 border-t border-navy-800 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col items-center gap-4">
              <Logo size="sm" className="text-white" />
            <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
              <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="footer-privacy-link">
                {t("auth.privacy_link")}
              </Link>
              <Link to="/terms" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="footer-terms-link">
                {t("tos.link")}
              </Link>
              <Link to="/hotel-terms" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="footer-hotel-terms-link">
                {t("htos.link")}
              </Link>
              <Link to="/about" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="footer-about-link">
                {t("nav.about")}
              </Link>
              <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors font-body" data-testid="footer-contact-link">
                {t("contact.link")}
              </Link>
            </div>
            <p className="text-sm text-slate-500 font-body">2025 Staffera. {t("footer.rights")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
